'use client'
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  MessageSquare,
  HeadphonesIcon,
  Building,
  Users,
  Globe
} from 'lucide-react';
import { APP_CONFIG } from '@/config';
import { useApp } from '@/pages/providers/AppProvider';

export function ContactPage() {
  const { siteSettings } = useApp();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const captchaRef = useRef<HTMLDivElement | null>(null);
  const [captchaWidgetId, setCaptchaWidgetId] = useState<number | null>(null);
  const [captchaSiteKey, setCaptchaSiteKey] = useState<string>('');
  const [captchaVersion, setCaptchaVersion] = useState<'v2' | 'v3'>('v2');

  // Use siteSettings from context or fall back to defaults
  const siteInfo = siteSettings || {
    companyName: APP_CONFIG.siteName,
    companyLocation: APP_CONFIG.company.location,
    mapUrl: APP_CONFIG.maps.embedUrl,
    googleMapsLink: APP_CONFIG.maps.link,
    email: APP_CONFIG.emails.contact,
    phone: APP_CONFIG.company.phone,
    address: APP_CONFIG.company.address,
    coordinates: APP_CONFIG.company.coordinates,
    // New structured contact fields
    mainPhone: APP_CONFIG.company.phone,
    supportPhone: APP_CONFIG.phones.support,
    mediaPressPhone: APP_CONFIG.phones.media,
    hrPhone: APP_CONFIG.phones.hr,
    supportEmail: APP_CONFIG.emails.support,
    mediaPressEmail: APP_CONFIG.emails.media,
    careersEmail: APP_CONFIG.emails.careers,
    addressLine1: 'Putalisadak, Kathmandu',
    addressLine2: 'Bagmati Province, Nepal',
    poBox: 'P.O. Box: 12345',
    weekdayHours: 'Monday - Friday: 9:00 AM - 6:00 PM',
    saturdayHours: 'Saturday: 10:00 AM - 4:00 PM',
    sundayHours: 'Sunday: Closed',
    holidayHours: 'Public Holidays: Closed'
  };

  useEffect(() => {
    // Recaptcha settings specifically (might still need to sync these if not in SiteSettings)
    const k = (siteInfo as any).recaptchaSiteKey || (siteInfo as any).recaptcha_site_key || '';
    if (k && typeof k === 'string') setCaptchaSiteKey(k);
    const v = (siteInfo as any).recaptchaVersion || (siteInfo as any).recaptcha_version;
    if (v === 'v3') setCaptchaVersion('v3');
  }, [siteInfo]);

  useEffect(() => {
    const envKey = (import.meta as any).env?.VITE_RECAPTCHA_SITE_KEY || '';
    if (envKey) setCaptchaSiteKey(envKey);
    const envVer = (import.meta as any).env?.VITE_RECAPTCHA_VERSION || '';
    if (envVer === 'v3') setCaptchaVersion('v3');
  }, []);

  useEffect(() => {
    const loadCaptcha = () => {
      const grecaptcha = (window as any).grecaptcha;
      if (!grecaptcha) return;
      if (!captchaRef.current || !captchaSiteKey) return;
      if (captchaWidgetId !== null) return;
      try {
        grecaptcha.ready(() => {
          if (!captchaRef.current) return;
          const id = grecaptcha.render(captchaRef.current, { sitekey: captchaSiteKey });
          setCaptchaWidgetId(id);
        });
      } catch {
        // Silent: will retry on re-render
      }
    };

    if (!(window as any).grecaptcha) {
      const script = document.createElement('script');
      script.src = captchaVersion === 'v3'
        ? `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(captchaSiteKey || '')}`
        : 'https://www.google.com/recaptcha/api.js';
      script.async = true;
      script.defer = true;
      script.onload = loadCaptcha;
      document.body.appendChild(script);
    } else {
      loadCaptcha();
    }
  }, [captchaSiteKey, captchaWidgetId, captchaVersion]);

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Head Office',
      details: [
        siteInfo.companyName || 'MegaJobNepal Pvt. Ltd.',
        siteInfo.addressLine1 || siteInfo.companyLocation || 'Putalisadak, Kathmandu, Nepal',
        siteInfo.addressLine2 || 'Bagmati Province, Nepal',
        siteInfo.poBox || 'P.O. Box: 12345'
      ]
    },
    {
      icon: Phone,
      title: 'Phone Numbers',
      details: [
        `Main: ${siteInfo.mainPhone || siteInfo.phone || '+977-1-4444567'}`,
        `Support: ${siteInfo.supportPhone || '+977-1-4444568'}`,
        `Media & Press: ${(siteInfo as any).mediaPressPhone || '+977-1-4444569'}`,
        `HR: ${siteInfo.hrPhone || '+977-1-4444570'}`
      ]
    },
    {
      icon: Mail,
      title: 'Email Addresses',
      details: [
        `General: ${siteInfo.email || 'info@megajobnepal.com'}`,
        `Support: ${siteInfo.supportEmail || 'support@megajobnepal.com'}`,
        `Media & Press: ${(siteInfo as any).mediaPressEmail || 'media@megajobnepal.com'}`,
        `Careers: ${siteInfo.careersEmail || 'hr@megajobnepal.com'}`
      ]
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: [
        siteInfo.weekdayHours || 'Monday - Friday: 9:00 AM - 6:00 PM',
        siteInfo.saturdayHours || 'Saturday: 10:00 AM - 4:00 PM',
        siteInfo.sundayHours || 'Sunday: Closed',
        siteInfo.holidayHours || 'Public Holidays: Closed'
      ]
    }
  ];

  const departments = [
    {
      icon: HeadphonesIcon,
      name: 'Customer Support',
      description: 'Get help with your account, job applications, or technical issues.',
      email: siteInfo.supportEmail || 'support@megajobnepal.com',
      phone: siteInfo.supportPhone || '+977-1-4444568',
      responseTime: '24 hours'
    },
    {
      icon: Globe,
      name: 'Media & Press',
      description: 'Media inquiries, press releases, and public relations matters.',
      email: (siteInfo as any).mediaPressEmail || 'media@megajobnepal.com',
      phone: (siteInfo as any).mediaPressPhone || '+977-1-4444569',
      responseTime: '24 hours'
    },
    {
      icon: Users,
      name: 'Human Resources',
      description: 'Career opportunities at MegaJobNepal and general HR inquiries.',
      email: siteInfo.careersEmail || 'hr@megajobnepal.com',
      phone: siteInfo.hrPhone || '+977-1-4444570',
      responseTime: '48 hours'
    },
    // Removed duplicate media card; consolidated above
  ];

  const [faqs, setFaqs] = useState<{ question: string, answer: string, category: string }[]>([]);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const data = await apiClient.getFaqs();
        if (Array.isArray(data) && data.length > 0) {
          // Flatten/normalize data if needed, ensuring category exists
          const sanitized = data.map(f => ({
            question: f.question,
            answer: f.answer,
            category: f.category || 'General'
          }));
          setFaqs(sanitized.slice(0, 6)); // Show locally up to 6 on contact page
        } else {
          // Fallback static faqs
          setFaqs([
            {
              category: 'Account',
              question: 'How do I create an account on MegaJobNepal?',
              answer: 'Click on "Login/Register" and choose between Job Seeker or Employer account. Fill in your details and verify your email to get started.'
            },
            {
              category: 'Payments',
              question: 'Is there a fee for job seekers to use the platform?',
              answer: 'No, MegaJobNepal is completely free for job seekers. You can search jobs, apply, and manage your profile at no cost.'
            },
            {
              category: 'Application',
              question: 'How long does it take for employers to respond to applications?',
              answer: 'Response times vary by employer, but most respond within 1-2 weeks. You can track your application status in your dashboard.'
            },
            {
              category: 'Language',
              question: 'Can I post jobs in languages other than English?',
              answer: 'Yes, we support job postings in Nepali and English. You can choose your preferred language when posting.'
            },
            {
              category: 'Services',
              question: 'Do you provide recruitment services?',
              answer: 'Yes, we offer premium recruitment services including candidate screening, interviews, and placement assistance. Contact our sales team for details.'
            },
            {
              category: 'Technical',
              question: 'I forgot my password, how can I recover it?',
              answer: 'Go to the login page and click "Forgot Password". Enter your registered email address to receive recovery instructions.'
            }
          ]);
        }
      } catch (error) {
        console.error('Failed to fetch FAQs:', error);
      }
    };
    fetchFaqs();
  }, []);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('📧 Contact form submitted', formData);

    if (isSubmitting) {
      console.log('⏸️ Already submitting, skipping');
      return;
    }

    // Basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.category || !formData.message.trim()) {
      console.error('❌ Validation failed: missing required fields');
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate message length (minimum 5 characters)
    if (formData.message.trim().length < 5) {
      console.error('❌ Validation failed: message too short');
      toast.error('Message must be at least 5 characters long');
      return;
    }

    console.log('✅ Validation passed, sending message...');

    try {
      setIsSubmitting(true);
      setCaptchaError(null);

      let captchaToken = '';
      if (captchaSiteKey) {
        const grecaptcha = (window as any).grecaptcha;
        if (captchaVersion === 'v3') {
          if (!grecaptcha) {
            setCaptchaError('Captcha failed to load');
            setIsSubmitting(false);
            return;
          }
          try {
            captchaToken = await grecaptcha.execute(captchaSiteKey, { action: 'contact' });
          } catch {
            setCaptchaError('Captcha verification failed');
            setIsSubmitting(false);
            return;
          }
        } else {
          if (captchaWidgetId !== null && grecaptcha) {
            captchaToken = grecaptcha.getResponse(captchaWidgetId);
          }
          if (!captchaToken) {
            setCaptchaError('Please complete the captcha verification');
            toast.error('Please complete the captcha verification');
            setIsSubmitting(false);
            return;
          }
        }
      }

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        category: formData.category,
        message: formData.message.trim(),
        captchaToken
      };

      console.log('📤 Sending payload:', payload);

      const response = await apiClient.sendContactMessage(payload);

      console.log('✅ Message sent successfully:', response);
      toast.success('Message sent successfully! We will get back to you soon.');

      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        category: '',
        message: ''
      });
      if (captchaWidgetId !== null && (window as any).grecaptcha) {
        (window as any).grecaptcha.reset(captchaWidgetId);
      }
    } catch (error: any) {
      console.error('❌ Failed to send message:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data
      });

      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to send message. Please try again later.';
      toast.error(errorMessage);
    } finally {
      console.log('🏁 Submit completed, resetting state');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-3">Get in Touch</h1>
            <p className="text-lg max-w-2xl mx-auto text-blue-100">
              We're here to help! Whether you have questions, feedback, or need support,
              our team is ready to assist you.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Contact Info */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-5">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4">
                      <IconComponent className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{info.title}</h3>
                    <div className="space-y-1">
                      {info.details.map((detail, idx) => (
                        <p key={idx} className="text-sm text-gray-600">{detail}</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form and Departments */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                  <span>Send us a Message</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <Input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Enter your full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject *
                    </label>
                    <Input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => handleInputChange('subject', e.target.value)}
                      placeholder="Enter message subject"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="support">Technical Support</SelectItem>
                        <SelectItem value="billing">Billing & Payments</SelectItem>
                        <SelectItem value="partnership">Partnership Inquiry</SelectItem>
                        <SelectItem value="feedback">Feedback & Suggestions</SelectItem>
                        <SelectItem value="media">Media & Press</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Type your message here..."
                      rows={6}
                      required
                    />
                  </div>
                  {captchaSiteKey && captchaVersion === 'v2' ? (
                    <div className="flex justify-center">
                      <div ref={captchaRef} className="mb-4" />
                    </div>
                  ) : (
                    <></>
                  )}
                  {captchaError && (
                    <p className="text-sm text-red-600">{captchaError}</p>
                  )}

                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Departments */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Departments</h2>
              <div className="space-y-4">
                {departments.map((dept, index) => {
                  const IconComponent = dept.icon;
                  return (
                    <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                      <CardContent className="p-4">
                        <div className="flex items-start space-x-4">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <IconComponent className="w-5 h-5 text-blue-600" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">{dept.name}</h3>
                            <p className="text-gray-600 mb-3">{dept.description}</p>
                            <div className="space-y-1">
                              <p className="text-sm">
                                <span className="font-medium">Email:</span> {dept.email}
                              </p>
                              <p className="text-sm">
                                <span className="font-medium">Phone:</span> {dept.phone}
                              </p>
                              <div className="flex items-center space-x-2 mt-2">
                                <Badge variant="secondary" className="text-xs">
                                  Response: {dept.responseTime}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">
              Find answers to the most common questions about MegaJobNepal.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {/* Job Seekers Column */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 border-b-2 border-blue-600 pb-1">For Job Seekers</h3>
              </div>
              <div className="space-y-4">
                {faqs.filter(f => f.category.toLowerCase().includes('seeker') || f.category.toLowerCase().includes('account') || f.category.toLowerCase().includes('application')).slice(0, 3).map((faq, index) => (
                  <Card key={`seeker-${index}`} className="hover:shadow-md transition-all duration-300 border-none shadow-sm bg-gray-50/50">
                    <CardContent className="p-4">
                      <h4 className="text-base font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                        {faq.question}
                      </h4>
                      <p className={`text-sm text-gray-600 leading-relaxed border-l-2 border-blue-200 pl-3 py-0.5 ${(faq as any).styling?.italic ? 'italic' : ''} ${(faq as any).styling?.bold ? 'font-bold' : ''} ${!(faq as any).styling ? 'italic' : ''}`}>
                        {faq.answer}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Employers Column */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 border-b-2 border-blue-600 pb-1">For Employers</h3>
              </div>
              <div className="space-y-4">
                {faqs.filter(f => f.category.toLowerCase().includes('employer') || f.category.toLowerCase().includes('service') || f.category.toLowerCase().includes('payment')).slice(0, 3).map((faq, index) => (
                  <Card key={`employer-${index}`} className="hover:shadow-md transition-all duration-300 border-none shadow-sm bg-gray-50/50">
                    <CardContent className="p-4">
                      <h4 className="text-base font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                        {faq.question}
                      </h4>
                      <p className={`text-sm text-gray-600 leading-relaxed border-l-2 border-blue-200 pl-3 py-0.5 ${(faq as any).styling?.italic ? 'italic' : ''} ${(faq as any).styling?.bold ? 'font-bold' : ''} ${!(faq as any).styling ? 'italic' : ''}`}>
                        {faq.answer}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-10">
            <p className="text-gray-600 mb-3 text-sm">Can't find what you're looking for?</p>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/contact/faqs'}>
              View All FAQs
            </Button>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Visit Our Office</h2>
            <p className="text-xl text-gray-600">
              We're located in the heart of Kathmandu. Drop by for a coffee and chat!
            </p>
          </div>

          <Card className="overflow-hidden">
            <div className="h-96">
              <iframe
                src={siteInfo.mapUrl || 'https://maps.google.com/maps?q=Putalisadak+Kathmandu+Nepal&output=embed'}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MegaJobNepal Office Location"
              />
            </div>
            <div className="p-6 bg-white border-t">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{siteInfo.companyName || 'MegaJobNepal Head Office'}</h3>
                  <p className="text-gray-600">
                    {(siteInfo.companyLocation || 'Putalisadak, Kathmandu, Nepal')}<br />
                    Bagmati Province, Nepal<br />
                    P.O. Box: 12345
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    onClick={() => window.open(siteInfo.googleMapsLink || 'https://goo.gl/maps/putalisadak-kathmandu', '_blank')}
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Get Directions
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      if (navigator.share) {
                        navigator.share({
                          title: 'MegaJobNepal Office Location',
                          text: `Visit our office at ${siteInfo.companyLocation || 'Putalisadak, Kathmandu'}`,
                          url: siteInfo.googleMapsLink || 'https://goo.gl/maps/putalisadak-kathmandu'
                        });
                      } else {
                        try {
                          if (navigator.clipboard && window.isSecureContext) {
                            await navigator.clipboard.writeText(`${siteInfo.companyLocation || 'Putalisadak, Kathmandu, Nepal'} - ${siteInfo.googleMapsLink || 'https://goo.gl/maps/putalisadak-kathmandu'}`);
                          } else {
                            // Fallback for non-secure contexts
                            const textArea = document.createElement('textarea');
                            textArea.value = `${siteInfo.companyLocation || 'Putalisadak, Kathmandu, Nepal'} - ${siteInfo.googleMapsLink || 'https://goo.gl/maps/putalisadak-kathmandu'}`;
                            document.body.appendChild(textArea);
                            textArea.select();
                            document.execCommand('copy');
                            document.body.removeChild(textArea);
                          }
                        } catch (error) {
                          console.log('Copy failed:', error);
                          // Show manual copy instruction
                          alert(`Please copy manually: ${siteInfo.companyLocation || 'Putalisadak, Kathmandu, Nepal'} - ${siteInfo.googleMapsLink || 'https://goo.gl/maps/putalisadak-kathmandu'}`);
                        }
                      }
                    }}
                  >
                    Share Location
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}

